import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, MapPin, Clock, LogOut, Shield, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';

const ActiveSessions = () => {
    const [sessions, setSessions] = useState([]);
    const [loginHistory, setLoginHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
    const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' or 'history'
    const [historyPage, setHistoryPage] = useState(1);
    const historyPerPage = 10;

    useEffect(() => {
        fetchData();
    }, []);



    const fetchData = async () => {
        try {
            setLoading(true);
            const [sessionsRes, historyRes] = await Promise.all([
                api.get('/auth/sessions'),
                api.get('/auth/login-history')
            ]);

            if (sessionsRes.data.success) {
                setSessions(sessionsRes.data.data);
            }
            if (historyRes.data.success) {
                setLoginHistory(historyRes.data.data);
            }
        } catch (error) {
            toast.error('Failed to load session data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutSession = async (sessionId) => {
        try {
            setActionLoading(sessionId);
            const { data } = await api.delete(`/auth/sessions/${sessionId}`);
            if (data.success) {
                toast.success('Session logged out successfully');
                fetchData();
            }
        } catch (error) {
            toast.error('Failed to logout session');
        } finally {
            setActionLoading(null);
        }
    };

    const handleLogoutAllOthers = async () => {
        if (!window.confirm('Are you sure you want to logout from all other devices?')) {
            return;
        }

        try {
            setActionLoading('logout-all');
            const { data } = await api.post('/auth/sessions/logout-all');
            if (data.success) {
                toast.success(data.message);
                fetchData();
            }
        } catch (error) {
            toast.error('Failed to logout other sessions');
        } finally {
            setActionLoading(null);
        }
    };

    const handleLogoutEverywhere = async () => {
        if (!window.confirm('This will log you out from ALL devices including this one. Continue?')) {
            return;
        }

        try {
            setActionLoading('logout-everywhere');
            const { data } = await api.post('/auth/sessions/logout-everywhere');
            if (data.success) {
                toast.success(data.message);
                // Redirect to login
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1500);
            }
        } catch (error) {
            toast.error('Failed to logout everywhere');
        } finally {
            setActionLoading(null);
        }
    };

    const getDeviceIcon = (device) => {
        switch (device?.toLowerCase()) {
            case 'mobile':
                return <Smartphone className="w-5 h-5" />;
            case 'tablet':
                return <Tablet className="w-5 h-5" />;
            default:
                return <Monitor className="w-5 h-5" />;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatRelativeTime = (date) => {
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);
        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
        return `${Math.floor(seconds / 86400)} days ago`;
    };

    // Pagination for login history
    const paginatedHistory = loginHistory.slice(
        (historyPage - 1) * historyPerPage,
        historyPage * historyPerPage
    );
    const totalHistoryPages = Math.ceil(loginHistory.length / historyPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Security & Sessions</h1>
                <p className="text-gray-600">Manage your active sessions and view login history</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('sessions')}
                    className={`pb-3 px-4 font-medium transition-colors ${activeTab === 'sessions'
                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Active Sessions ({sessions.length})
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-3 px-4 font-medium transition-colors ${activeTab === 'history'
                        ? 'text-indigo-600 border-b-2 border-indigo-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Login History
                </button>
            </div>

            {/* Active Sessions Tab */}
            {activeTab === 'sessions' && (
                <div className="space-y-6">
                    {/* Security Actions */}
                    <Card>
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-red-100 rounded-lg">
                                    <Shield className="w-6 h-6 text-red-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">Security Actions</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Logout from other devices if you suspect unauthorized access
                                    </p>
                                    <div className="flex gap-3">
                                        <Button
                                            onClick={handleLogoutAllOthers}
                                            variant="outline"
                                            className="text-sm"
                                            disabled={actionLoading !== null}
                                        >
                                            {actionLoading === 'logout-all' ? 'Logging out...' : 'Logout Other Sessions'}
                                        </Button>
                                        <Button
                                            onClick={handleLogoutEverywhere}
                                            variant="danger"
                                            className="text-sm"
                                            disabled={actionLoading !== null}
                                        >
                                            {actionLoading === 'logout-everywhere' ? 'Logging out...' : 'Logout Everywhere'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Sessions List */}
                    <div className="space-y-4">
                        {sessions.length === 0 ? (
                            <Card>
                                <div className="p-8 text-center text-gray-500">
                                    No active sessions found
                                </div>
                            </Card>
                        ) : (
                            sessions.map((session) => (
                                <Card key={session._id}>
                                    <div className="p-6">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4">
                                                <div className="p-3 bg-indigo-100 rounded-lg">
                                                    {getDeviceIcon(session.device)}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">
                                                        {session.browser} on {session.os}
                                                    </h3>
                                                    <div className="space-y-1 text-sm text-gray-600">
                                                        <div className="flex items-center gap-2">
                                                            <MapPin className="w-4 h-4" />
                                                            <span>{session.ipAddress}</span>
                                                            {session.location !== 'Unknown' && (
                                                                <span>• {session.location}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-4 h-4" />
                                                            <span>Last active: {formatRelativeTime(session.lastActivity)}</span>
                                                        </div>
                                                    </div>
                                                    {session.isCurrent && (
                                                        <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                                            Current Session
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {!session.isCurrent && (
                                                <Button
                                                    onClick={() => handleLogoutSession(session._id)}
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-600 hover:bg-red-50"
                                                    disabled={actionLoading !== null}
                                                >
                                                    <LogOut className="w-4 h-4 mr-1" />
                                                    {actionLoading === session._id ? 'Logging out...' : 'Logout'}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Login History Tab */}
            {activeTab === 'history' && (
                <div className="space-y-4">
                    {loginHistory.length === 0 ? (
                        <Card>
                            <div className="p-8 text-center text-gray-500">
                                No login history available
                            </div>
                        </Card>
                    ) : (
                        <>
                            {paginatedHistory.map((login, index) => (
                                <Card key={index}>
                                    <div className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className={`p-3 rounded-lg ${login.success ? 'bg-green-100' : 'bg-red-100'}`}>
                                                {login.success ? (
                                                    getDeviceIcon(login.device)
                                                ) : (
                                                    <AlertTriangle className="w-5 h-5 text-red-600" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="font-semibold text-gray-900">
                                                        {login.browser} on {login.os}
                                                    </h3>
                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${login.success
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                        }`}>
                                                        {login.success ? 'Successful' : 'Failed'}
                                                    </span>
                                                </div>
                                                <div className="space-y-1 text-sm text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{formatDate(login.timestamp)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-4 h-4" />
                                                        <span>{login.ipAddress}</span>
                                                        {login.location !== 'Unknown' && (
                                                            <span>• {login.location}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ))}

                            {/* Pagination Controls */}
                            {totalHistoryPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6">
                                    <button
                                        onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                                        disabled={historyPage === 1}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-600">
                                        Page {historyPage} of {totalHistoryPages}
                                    </span>
                                    <button
                                        onClick={() => setHistoryPage(prev => Math.min(totalHistoryPages, prev + 1))}
                                        disabled={historyPage === totalHistoryPages}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ActiveSessions;
